/**
 * Federal income tax computation (spec §9). The single most valuable and most
 * error-prone module — kept isolated and driven entirely by dated constants.
 *
 * Handles:
 *   - Ordinary brackets + standard/itemized deduction (greater of), incl. the
 *     additional 65+ deduction and the OBBBA senior bonus deduction with its
 *     MAGI phase-out.
 *   - LTCG / qualified dividends taxed in a separate rate schedule that STACKS
 *     on top of ordinary taxable income (0%/15%/20%).
 *   - Social Security taxability (two-tier provisional income).
 *   - Net Investment Income Tax (3.8% over fixed MAGI thresholds).
 *
 * Additional Medicare Tax and FICA/SE tax live in fica.ts (earned income).
 */
import type { FilingStatus } from '../../model/types';
import { federalTaxYear, type Bracket } from '../data/federal';
import { taxableSocialSecurity, type SsTaxabilityResult } from './ssTaxability';

export interface FederalTaxInput {
  year: number;
  filingStatus: FilingStatus;
  /** Ages of household members at year-end; used for the 65+ deductions. */
  ages: number[];
  /** Wages, pensions, pre-tax withdrawals, Roth conversions, taxable interest, STCG, etc. */
  ordinaryIncome: number;
  qualifiedDividends: number;
  longTermCapitalGains: number;
  /** Gross Social Security benefits received (before taxability haircut). */
  socialSecurityBenefits: number;
  taxExemptInterest?: number;
  /** If provided and larger than the standard deduction, itemized is used. */
  itemizedDeductions?: number;
  /**
   * Net investment income for NIIT (QDI + LTCG + STCG + taxable interest + ...).
   * Defaults to qualifiedDividends + longTermCapitalGains when omitted.
   */
  netInvestmentIncome?: number;
  /**
   * The plan's general inflation assumption. For years past the latest defined
   * tax year, brackets/deductions/LTCG breakpoints are indexed by this rate
   * (as the IRS does annually); omitted = the last known law held flat.
   */
  inflationRate?: number;
}

export interface FederalTaxResult {
  agi: number;
  taxableIncome: number;
  ordinaryTaxableIncome: number;
  preferentialIncome: number; // QDI + LTCG actually taxed at preferential rates
  standardDeduction: number;
  itemizedDeduction: number;
  deductionUsed: number;
  seniorBonusDeduction: number;
  ss: SsTaxabilityResult;
  ordinaryTax: number;
  capitalGainsTax: number;
  niit: number;
  totalTax: number;
  marginalOrdinaryRate: number;
  effectiveRate: number; // totalTax / (AGI) guarded for 0
}

/** Tax on `amount` of ordinary taxable income using a bracket schedule. */
export function ordinaryTaxOn(amount: number, brackets: Bracket[]): number {
  if (amount <= 0) return 0;
  let tax = 0;
  for (let i = 0; i < brackets.length; i++) {
    const b = brackets[i]!;
    if (amount <= b.floor) break;
    const next = brackets[i + 1];
    const ceiling = next ? next.floor : Infinity;
    const slice = Math.min(amount, ceiling) - b.floor;
    if (slice > 0) tax += slice * b.rate;
  }
  return tax;
}

/** Marginal ordinary rate at a given taxable income. */
export function marginalRateAt(amount: number, brackets: Bracket[]): number {
  let rate = brackets[0]!.rate;
  for (const b of brackets) {
    if (amount >= b.floor) rate = b.rate;
    else break;
  }
  return rate;
}

/**
 * LTCG/QDI tax with correct stacking: preferential income sits ON TOP of
 * ordinary taxable income and is taxed through the 0/15/20 breakpoints by its
 * position in TOTAL taxable income (spec §9 — the 0% bracket is central to any
 * credible early-retirement drawdown strategy).
 */
export function capitalGainsTaxStacked(
  ordinaryTaxableIncome: number,
  preferentialIncome: number,
  rate0Ceiling: number,
  rate15Ceiling: number,
): number {
  if (preferentialIncome <= 0) return 0;
  let bottom = Math.max(0, ordinaryTaxableIncome);
  let remaining = preferentialIncome;
  let tax = 0;

  const zeroRoom = Math.max(0, rate0Ceiling - bottom);
  const at0 = Math.min(remaining, zeroRoom);
  remaining -= at0;
  bottom += at0;

  const fifteenRoom = Math.max(0, rate15Ceiling - bottom);
  const at15 = Math.min(remaining, fifteenRoom);
  tax += at15 * 0.15;
  remaining -= at15;

  tax += Math.max(0, remaining) * 0.2;
  return tax;
}

function seniorBonus(
  year: number,
  filingStatus: FilingStatus,
  ages: number[],
  magi: number,
): number {
  const fy = federalTaxYear(year);
  const sb = fy.seniorBonusDeduction;
  if (year > sb.expiresAfterYear) return 0;
  const eligibleCount = ages.filter((a) => a >= 65).length;
  if (eligibleCount === 0) return 0;

  const start = sb.phaseoutStart[filingStatus];
  const over = Math.max(0, magi - start);
  // Phase-out is computed PER eligible person: reduce each person's $6,000 by
  // 6% of the joint MAGI excess (spec-flagged nuance from the research).
  const perPersonReduction = over * sb.phaseoutRate;
  const perPerson = Math.max(0, sb.perPerson - perPersonReduction);
  return perPerson * eligibleCount;
}

export function computeFederalTax(input: FederalTaxInput): FederalTaxResult {
  const fy = federalTaxYear(input.year, input.inflationRate);
  const fs = input.filingStatus;
  const taxExemptInterest = input.taxExemptInterest ?? 0;

  const otherIncomeExclSS =
    input.ordinaryIncome + input.qualifiedDividends + input.longTermCapitalGains;

  const ss = taxableSocialSecurity(
    input.year,
    fs,
    otherIncomeExclSS,
    taxExemptInterest,
    input.socialSecurityBenefits,
  );

  const agi = otherIncomeExclSS + ss.taxableBenefits;
  const magi = agi + taxExemptInterest;

  // Deductions: greater of standard (with 65+ additions) or itemized, plus the
  // separate senior bonus deduction (available to itemizers and non-itemizers).
  const eligible65 = input.ages.filter((a) => a >= 65).length;
  const standardDeduction =
    fy.standardDeduction[fs] + eligible65 * fy.additional65Deduction[fs];
  const itemizedDeduction = input.itemizedDeductions ?? 0;
  const baseDeduction = Math.max(standardDeduction, itemizedDeduction);
  const seniorBonusDeduction = seniorBonus(input.year, fs, input.ages, magi);
  const deductionUsed = baseDeduction + seniorBonusDeduction;

  const taxableIncome = Math.max(0, agi - deductionUsed);

  // Preferential income is the top slice of taxable income.
  const preferentialIncome = Math.min(
    Math.max(0, input.qualifiedDividends + input.longTermCapitalGains),
    taxableIncome,
  );
  const ordinaryTaxableIncome = taxableIncome - preferentialIncome;

  const brackets = fy.ordinaryBrackets[fs];
  const ordinaryTax = ordinaryTaxOn(ordinaryTaxableIncome, brackets);

  const bp = fy.ltcgBreakpoints[fs];
  const capitalGainsTax = capitalGainsTaxStacked(
    ordinaryTaxableIncome,
    preferentialIncome,
    bp.rate0Ceiling,
    bp.rate15Ceiling,
  );

  // NIIT: 3.8% of the lesser of NII or MAGI over the (fixed) threshold.
  const nii = input.netInvestmentIncome ?? input.qualifiedDividends + input.longTermCapitalGains;
  const niitBase = Math.min(Math.max(0, nii), Math.max(0, magi - fy.niit.magiThreshold[fs]));
  const niit = niitBase * fy.niit.rate;

  const totalTax = ordinaryTax + capitalGainsTax + niit;

  return {
    agi,
    taxableIncome,
    ordinaryTaxableIncome,
    preferentialIncome,
    standardDeduction,
    itemizedDeduction,
    deductionUsed,
    seniorBonusDeduction,
    ss,
    ordinaryTax,
    capitalGainsTax,
    niit,
    totalTax,
    marginalOrdinaryRate: marginalRateAt(ordinaryTaxableIncome, brackets),
    effectiveRate: agi > 0 ? totalTax / agi : 0,
  };
}
